import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from 'react';
import enStrings from './strings/en.json';
import { API_PREFIX } from '../api/endpoints/api.constants';

// ─── Types ───────────────────────────────────────────────────────────────────

export type LangStrings = Record<string, string>;
export type TrVars = Record<string, unknown>;

export interface LanguageContextValue {
  locale: string;
  strings: LangStrings;
  setLocale: (code: string) => void;
  showLangMode: boolean;
  isSuperAdmin: boolean;
  canWriteMultilang: boolean;
  isLoading: boolean;
  isRTL: boolean;
  tenantId: number | null;
  /** Reload strings from API (e.g. after saving an override) */
  refreshStrings: () => void;
  /**
   * Translate a key. The optional 2nd arg may be either a default string
   * (shown when the key is missing from all string tables) OR an
   * interpolation vars map. A 3rd arg can carry vars alongside a default.
   *   t('key')
   *   t('key', 'Default text')
   *   t('key', { name: 'Ada' })
   *   t('key', 'Hello {{name}}', { name: 'Ada' })
   */
  t: (
    key: string,
    defaultOrVars?: string | TrVars,
    vars?: TrVars,
  ) => string;
}

// Locales that read right-to-left
const RTL_LOCALES = new Set(['ar', 'he', 'fa', 'ur', 'ku', 'ps', 'dv', 'sd']);

// ─── Context ─────────────────────────────────────────────────────────────────

export const LanguageContext = createContext<LanguageContextValue | null>(null);

// ─── Interpolate vars in string ───────────────────────────────────────────────

// Accepts both `{name}` and `{{name}}` placeholders.
function interpolate(template: string, vars?: TrVars): string {
  if (!vars) return template;
  return template.replace(/\{\{\s*(\w+)\s*\}\}|\{(\w+)\}/g, (match, dbl, single) => {
    const key = dbl ?? single;
    return key in vars ? String(vars[key]) : match;
  });
}

// ─── Cache helpers ────────────────────────────────────────────────────────────

const CACHE_PREFIX = 'ilearn_lang_';
const LOCALE_KEY = 'ilearn_active_locale';

function getCacheKey(locale: string, tenantId: number | null) {
  return `${CACHE_PREFIX}${locale}_t${tenantId ?? 'global'}`;
}

function readCache(locale: string, tenantId: number | null): LangStrings | null {
  try {
    const raw = localStorage.getItem(getCacheKey(locale, tenantId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeCache(locale: string, tenantId: number | null, data: LangStrings) {
  try {
    localStorage.setItem(getCacheKey(locale, tenantId), JSON.stringify(data));
  } catch {}
}

// ─── Module-level string store + tr() ─────────────────────────────────────────
// tr() works anywhere (components, helpers, module-level label maps). The store
// is seeded synchronously from the cached overrides of the active locale, so
// modules evaluated at startup already see translated strings; switching
// language reloads the page so module-level constants re-evaluate.

function initialTenantId(): number | null {
  try {
    const raw = localStorage.getItem('tenant_id');
    const n = raw ? Number(raw) : NaN;
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function initialLocale(): string {
  try {
    return localStorage.getItem(LOCALE_KEY) || 'en';
  } catch {
    return 'en';
  }
}

let activeStrings: LangStrings = {
  ...(enStrings as LangStrings),
  ...(readCache(initialLocale(), initialTenantId()) ?? readCache(initialLocale(), null) ?? {}),
};

function setActiveStrings(overrides: LangStrings) {
  activeStrings = { ...(enStrings as LangStrings), ...overrides };
}

/**
 * Translate outside React hooks: tr('key', 'Default text', { name: 'Ada' }).
 * Same lookup and interpolation as t().
 */
export function tr(key: string, fallback?: string, vars?: TrVars): string {
  const raw = activeStrings[key] ?? fallback ?? key;
  return interpolate(raw, vars);
}

/** Locale for dates and numbers in the active UI language (Arabic keeps Latin digits). */
export function appLocale(): string {
  const l = initialLocale();
  return l === 'ar' ? 'ar-u-nu-latn' : l;
}

let pluralRules: Intl.PluralRules | null = null;

/**
 * Count-aware noun: trn('ui.member', n, 'member', 'members').
 * `key` holds the singular; other forms live at `${key}_${category}` using the
 * locale's CLDR plural categories (Russian few/many, Arabic two/few/many) and
 * fall back to `${key}_other`. The locale is fixed per page load (setLocale reloads).
 */
export function trn(key: string, count: number | null | undefined, one: string, other: string, vars?: TrVars): string {
  pluralRules ??= new Intl.PluralRules(initialLocale());
  const n = count ?? 0;
  const cat = pluralRules.select(n);
  const raw = cat === 'one'
    ? activeStrings[key] ?? one
    : activeStrings[`${key}_${cat}`] ?? activeStrings[`${key}_other`] ?? other;
  return interpolate(raw, { n, ...vars });
}

// ─── API helpers ──────────────────────────────────────────────────────────────

const API_BASE = API_PREFIX;

async function fetchOverrides(locale: string, tenantId: number | null): Promise<LangStrings | null> {
  // Public endpoint — guests (homepage, login) need translations too.
  // Returns null (not {}) when the request fails so callers can keep whatever
  // good strings they already have instead of wiping them to English on a
  // transient API blip. Retries network/5xx errors with a short backoff.
  const token = localStorage.getItem('accessToken');
  const url = tenantId
    ? `${API_BASE}/languages/${locale}/strings?tenant_id=${tenantId}`
    : `${API_BASE}/languages/${locale}/strings`;
  const MAX_ATTEMPTS = 3;
  for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt++) {
    try {
      // Always name the tenant: without X-Tenant-ID the backend resolves it
      // from the API host's subdomain, which can be a different tenant than
      // the one the user is on (and therefore different/empty translations).
      const headers: Record<string, string> = {
        'X-Tenant-ID': localStorage.getItem('tenant_id') || '1',
      };
      if (token) headers.Authorization = `Bearer ${token}`;
      const res = await fetch(url, { headers });
      if (res.ok) {
        const data = await res.json();
        return data.strings ?? {};
      }
      // 4xx is a definitive answer — don't hammer the API for it.
      if (res.status >= 400 && res.status < 500) return null;
    } catch {
      // network error / connection refused — fall through and retry
    }
    if (attempt < MAX_ATTEMPTS - 1) {
      await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
    }
  }
  return null;
}

// ─── Provider ─────────────────────────────────────────────────────────────────

interface LanguageProviderProps {
  children: ReactNode;
  tenantId?: number | null;
  isSuperAdmin?: boolean;
  /** User has lang:showlang permission — can activate ?showlang=true debug mode */
  hasShowLangPermission?: boolean;
  /** User has content:multilang_write permission — sees EN/AR/FR tabs on data-entry fields */
  canWriteMultilang?: boolean;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
  tenantId = null,
  isSuperAdmin = false,
  hasShowLangPermission = false,
  canWriteMultilang = false,
}) => {
  const [locale, setLocaleState] = useState<string>(
    () => localStorage.getItem(LOCALE_KEY) || 'en'
  );
  const [overrides, setOverrides] = useState<LangStrings>({});
  const [isLoading, setIsLoading] = useState(true);
  const refreshCount = useRef(0);

  const isRTL = RTL_LOCALES.has(locale);

  // Set document direction and language whenever locale changes
  useEffect(() => {
    const html = document.documentElement;
    html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
    html.setAttribute('lang', locale);
  }, [locale, isRTL]);

  // showlang=true is only available to users with lang:showlang permission (no localhost bypass)
  const showLangMode =
    hasShowLangPermission &&
    new URLSearchParams(window.location.search).get('showlang') === 'true';

  // Merged strings: en.json defaults + API overrides
  const strings: LangStrings = { ...(enStrings as LangStrings), ...overrides };

  // Bumped when the strings change after the tree has rendered, so components
  // that call tr() during render pick up the new text.
  const [stringsVersion, setStringsVersion] = useState(0);

  const loadStrings = useCallback(async (lang: string, tid: number | null) => {
    setIsLoading(true);
    // Try cache first
    const cached = readCache(lang, tid);
    if (cached) {
      setOverrides(cached);
      setActiveStrings(cached);
      setIsLoading(false);
    }
    // Always refresh from API. If the request fails, keep whatever we already
    // have (cached strings or the en.json defaults) rather than overwriting a
    // good cache with an empty result.
    const fresh = await fetchOverrides(lang, tid);
    if (fresh === null) {
      setIsLoading(false);
      return;
    }
    const changed = JSON.stringify(fresh) !== JSON.stringify(cached ?? {});
    setOverrides(fresh);
    setActiveStrings(fresh);
    writeCache(lang, tid, fresh);
    setIsLoading(false);
    if (changed) setStringsVersion((v) => v + 1);
    // UAT DEF-PG-004: on the first load of a non-English locale on this device there is no cached
    // bundle yet, so everything rendered through the module-level tr() store (and module-level
    // label maps) came out English and only re-rendered by accident. Mirror setLocale(): now that
    // the bundle is cached, reload once so the whole tree renders from the translated store.
    if (!cached && changed && lang !== 'en') {
      const guard = `ilearn_lang_reloaded_${getCacheKey(lang, tid)}`;
      try {
        if (!sessionStorage.getItem(guard)) {
          sessionStorage.setItem(guard, '1');
          window.location.reload();
        }
      } catch {}
    }
  }, []);

  useEffect(() => {
    loadStrings(locale, tenantId);
  }, [locale, tenantId, loadStrings, refreshCount.current]);

  const setLocale = useCallback((code: string) => {
    if (code === locale) return;
    // Fetch + cache the new language first, then reload so every string —
    // including module-level label maps — renders in the new language.
    fetchOverrides(code, tenantId)
      .then((fresh) => { if (fresh) writeCache(code, tenantId, fresh); })
      .finally(() => {
        localStorage.setItem(LOCALE_KEY, code);
        setLocaleState(code);
        window.location.reload();
      });
  }, [locale, tenantId]);

  const refreshStrings = useCallback(() => {
    refreshCount.current += 1;
    // Clear cache so fresh data is fetched
    try {
      localStorage.removeItem(getCacheKey(locale, tenantId));
    } catch {}
    loadStrings(locale, tenantId);
  }, [locale, tenantId, loadStrings]);

  const t = useCallback(
    (
      key: string,
      defaultOrVars?: string | TrVars,
      vars?: TrVars,
    ): string => {
      // The 2nd arg is either a default string or an interpolation map.
      const defaultValue =
        typeof defaultOrVars === 'string' ? defaultOrVars : undefined;
      const interpVars =
        typeof defaultOrVars === 'string' ? vars : defaultOrVars;
      const raw =
        strings[key] ?? (enStrings as LangStrings)[key] ?? defaultValue ?? key;
      return interpolate(raw, interpVars);
    },
    [strings]
  );

  return (
    <LanguageContext.Provider
      value={{ locale, strings, setLocale, showLangMode, isSuperAdmin, canWriteMultilang, isLoading, isRTL, tenantId, refreshStrings, t }}
    >
      <React.Fragment key={stringsVersion}>{children}</React.Fragment>
    </LanguageContext.Provider>
  );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used inside <LanguageProvider>');
  return ctx;
}

/** Shorthand — use this in components */
export function useT() {
  const { t, showLangMode, locale, isRTL, isSuperAdmin, canWriteMultilang } = useLanguage();
  return { t, showLangMode, locale, isRTL, isSuperAdmin, canWriteMultilang };
}

export * from './Tr';
